import React, { useState, useEffect } from "react";
import svgPaths from "./Palette-Icon.ts";

export function AnimatedPaletteIcon({ 
  size = 300,
  className = ""
}: { 
  size?: number;
  className?: string;
}) {
  const [isHovering, setIsHovering] = useState(false);
  const [key, setKey] = useState(0);
  const [bottomMargin, setBottomMargin] = useState('-4px');
  const [leftMargin, setLeftMargin] = useState('4px');
  
  // Original SVG dimensions
  const originalWidth = 303;
  const originalHeight = 300;

  // Update bottom margin based on screen width
  useEffect(() => {
    const updateMargin = () => {
      setBottomMargin(window.innerWidth <= 720 ? '-2px' : '-4px');
      setLeftMargin(window.innerWidth <= 720 ? '4px' : '4px');
    };
    
    // Set initial margin
    updateMargin();
    
    // Add event listener
    window.addEventListener('resize', updateMargin);
    
    // Cleanup
    return () => window.removeEventListener('resize', updateMargin);
  }, []);

  // Handle mouse enter to start animation
  const handleMouseEnter = () => {
    setIsHovering(true);
  };

  // Handle mouse leave to stop animation
  const handleMouseLeave = () => {
    setIsHovering(false);
    // Increment key to force a reset when we hover again
    setKey((prev) => prev + 1);
  };

  return (
    <svg
      key={key}
      width={size}
      height={size}
      className={`cursor-pointer ${className}`}
      fill="none"
      preserveAspectRatio="xMidYMid meet"
      viewBox={`0 0 ${originalWidth} ${originalHeight}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{ overflow: 'visible', marginBottom: bottomMargin, marginLeft: leftMargin }}
    >
      <defs>
        <style>{`
          /* Animation for color 1 (first in sequence) */
          @keyframes fillColor1 {
            0% { fill: white; }
            5%, 20% { fill: #FF48A8; }
            25%, 100% { fill: white; }
          }
          
          /* Animation for color 2 (second in sequence) */
          @keyframes fillColor2 {
            0%, 33% { fill: white; }
            38%, 53% { fill: #FFB700; }
            58%, 100% { fill: white; }
          }
          
          /* Animation for color 3 (third in sequence) */
          @keyframes fillColor3 {
            0%, 66% { fill: white; }
            71%, 86% { fill: #54D2FF; }
            91%, 100% { fill: white; }
          }
          
          /* Animation for palette gentle swing - simplifying for smoothness */
          @keyframes swingPalette {
            0% { transform: rotate(0deg); }
            50% { transform: rotate(5deg); }
            100% { transform: rotate(-5deg); }
          }
        `}</style>
      </defs>

      <g 
        id="Paint-Palette"
        style={{
          transformOrigin: 'center center',
          animation: isHovering 
            ? 'swingPalette 1.8s infinite alternate ease-in-out' 
            : 'none'
        }}
      >
        <rect fill="white" height="300" width="303" />

        {/* Color circles with conditional animation classes */}
        <circle
          cx="87.5"
          cy="182.5"
          fill="white"
          id="Color1"
          r="14.5"
          style={{
            animation: isHovering
              ? "fillColor1 1.2s linear infinite"
              : "none"
          }}
        />

        <circle
          cx="136.5"
          cy="75.5"
          fill="white"
          id="Color2"
          r="13.5"
          style={{
            animation: isHovering
              ? "fillColor2 1.2s linear infinite"
              : "none"
          }}
        />

        <circle
          cx="199.5"
          cy="94.5"
          fill="white"
          id="Color3"
          r="13.5"
          style={{
            animation: isHovering
              ? "fillColor3 1.2s linear infinite"
              : "none"
          }}
        />

        <path
          d={svgPaths.p263c3d80}
          fill="black"
          id="Vector"
        />
        <path
          d={svgPaths.p25208f80}
          fill="black"
          id="Vector_2"
        />
        <path
          d={svgPaths.p22eaec00}
          fill="black"
          id="Vector_3"
        />
        <path
          d={svgPaths.p32e77900}
          fill="black"
          id="Vector_4"
        />
        <path
          d={svgPaths.p1d078580}
          fill="black"
          id="Vector_5"
        />
        <path
          d={svgPaths.p8400800}
          fill="black"
          id="Vector_6"
        />
      </g>
    </svg>
  );
}

// Also export as default for backward compatibility
export default AnimatedPaletteIcon;